import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Title from "../../../components/common/Title";
import SubTitle from "../../../components/common/SubTitle";
import { CampaignButtonVariant } from "../../../components/common/Button/Button";
import CampaignCard from "../../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../../components/common/CampaignCard/CampaignCardSkeleton";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../utils/Images";
import { getCampaigns } from "../../../api/services/campaignService";
import { canViewPrivateCampaign } from "../../../utils/authUtils";
import { matchesCategory } from "../../../utils/helper";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const UrgentNeedsCampaign = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow1200 = useMediaQuery("(max-width: 1200px)");
  const isAbove1440 = useMediaQuery("(min-width: 1440px)");
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchUrgentCampaigns = async () => {
      try {
        setLoading(true);
        const response = await getCampaigns();
        if (isMounted) {
          const allCampaigns = response?.data || [];
          // First filter by privacy: show public campaigns or private campaigns if user is admin
          const privacyFiltered = allCampaigns.filter((c) => {
            // If campaign is public (IsPrivate === 0), always show
            if (c.IsPrivate === 0 || !c.IsPrivate) {
              return true;
            }
            // If campaign is private (IsPrivate === 1), only show if user is admin
            if (c.IsPrivate === 1) {
              return canViewPrivateCampaign(user, isAuthenticated);
            }
            return true; // Default: show if IsPrivate is not set
          });
          // Filter for Urgent category
          const urgentCampaigns = privacyFiltered.filter(
            (campaign) => matchesCategory(campaign.category, "Urgent")
          );
          // Sort by latest (createdAt or updatedAt) - most recent first
          const sorted = urgentCampaigns.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
          });
          // Get latest 3
          setCampaigns(sorted.slice(0, 3));
        }
      } catch (error) {
        console.error("Error fetching urgent campaigns:", error);
        if (isMounted) {
          setCampaigns([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUrgentCampaigns();
    return () => {
      isMounted = false;
    };
  }, [user, isAuthenticated]);

  const descriptionText = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    // Use Normal Campaign description (id: 104), same as other urgent sections
    const section = sections.find((s) => s?.id === 104);
    return section?.description || "";
  }, [cmsHome]);
  return (
    <div
      style={{
        width: isMobile ? "100%" : "100%",
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "rgba(232, 232, 232, 0.36)",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "center",
        marginTop: isMobile ? "10px" : "40px",
        marginBottom: "40px",
        padding: isBelow1200 ? "16px" : " 40px",
        position: "relative", // Added for absolute positioning of decorative elements
        overflow: "hidden", // Added to contain the decorative elements
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap:"100px"
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "center" : "start",
            textAlign: "left",
          }}
        >
          {/* Title */}
          <Box
            sx={{
              mb: isBelow1200 ? "8px" : "10px",
              zIndex: 2,
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontSize: isBelow1200 ? "20px" : "36px",
                fontWeight: isBelow1200 ? "800" : "700",
                color: "#1D1D1B",
                lineHeight: "140%",
              }}
            >
              Urgent Campaign
            </Typography>
          </Box>

          {/* Subtitle */}
          <Box
            sx={{
              width: "100%",
              mb: isBelow1200 ? "20px" : "38px",
              fontSize: "14px",
            }}
          >
            <Typography
              sx={{
                fontSize: isBelow1200 ? "10px" : "14px",
                color: "#666666",
                textAlign: "left",
                fontWeight: 400,
                letterSpacing: "-0.2px",
              }}
            >
              {descriptionText}
            </Typography>
          </Box>
        </Box>
        {/* Button Box */}
        <Box sx={{ display: isMobile ? "none" : "block" }}>
          <CampaignButtonVariant
            text="See All Campaigns"
            isMobile={isMobile}
            onClick={() => navigate("/campaigns")}
          />
        </Box>
      </Box>

      {/* Red Hearts - Separate from title, mobile only */}
      <Box
        component="img"
        src={Images.redHearts}
        alt="Red Hearts"
        sx={{
          position: "absolute",
          top: isBelow1200 ? "14px" : "50px",
          right: isMobile ? "86px" : "40px",
          width: isBelow1200 ? "30px" : "28px",
          height: isBelow1200 ? "30px" : "28px",
          display: isMobile ? "block" : "none", // Show only on mobile
          zIndex: 2,
        }}
      />

      {/* Campaign Cards Section */}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-evenly",
          alignItems: "center",
          gap: isMobile ? "16px" : "24px",
          marginBottom: isMobile ? "20px" : "48px",
          paddingLeft: isMobile ? "16px" : "0px",
          paddingRight: isMobile ? "0px" : "0px",
          overflowX: isMobile ? "auto" : isAbove1440 ? "hidden" : "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="hide-scrollbar"
      >
        {loading ? (
          // Show skeleton loaders while loading
          Array.from({ length: 3 }).map((_, index) => (
            <CampaignCardSkeleton key={`skeleton-${index}`} />
          ))
        ) : campaigns.length > 0 ? (
          // Show campaigns if available
          (isAbove1440 ? campaigns.slice(0, 3) : campaigns).map((campaign) => (
            <CampaignCard key={campaign.id} campaignData={campaign} />
          ))
        ) : (
          // Show message if no urgent campaigns found
          <Typography
            sx={{
              color: "#666666",
              textAlign: "center",
              fontSize: "14px",
              padding: "20px",
            }}
          >
            No urgent campaigns available at the moment.
          </Typography>
        )}
      </div>

      <Box sx={{ display: isMobile ? "block" : "none", mt: isMobile ? "30px" : 0 }}>
        <CampaignButtonVariant
          text="View All"
          isMobile={isMobile}
          onClick={() => navigate("/campaigns")}
        />
      </Box>

      {/* String Hand Icon - Bottom Left */}
      <Box
        component="img"
        src={Images.stringHand}
        alt="String Hand"
        sx={{
          position: "absolute",
          bottom: "16px",
          left: "0px",
          width: "43px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "block" : "none", // Show only on mobile
          transform: "scaleX(-1)", // Invert horizontally
          pointerEvents: "none", // Don't interfere with clicks
        }}
      />
    </div>
  );
};

export default UrgentNeedsCampaign;
